//react/redux
import { useState } from "react";

//Store
import { store } from "../../../lib/store.js";
import { postImage } from "../../../lib/slices/assure.js";

//Libs
import { FiArrowRight } from "react-icons/fi";
import { datadogLogs } from "@datadog/browser-logs";

//components
import CaptureSelfie from "../CaptureSelfie.js";

//hooks
import ImagePreview from "../../../Hooks/ImagePreview.js";
import AnalyzingLoader from "../../../Hooks/AnalyzingLoader.js";
import { useCameraReady } from "../../../Hooks/useCameraReady.js";

//Components
import Header from "../../Common/header.js";
import toast from "react-hot-toast";

const ManualCapture = ({
    instance,
    responseScan,
    tenantColor,
    tenantImage,
    tenantName,
}) => {
    const [imageToken, setImageToken] = useState(false);
    const [imageFront, setImageFront] = useState({});
    const [imageBack, setImageBack] = useState({});
    const [openSelfie, setOpenSelfie] = useState(false);
    const [loading, setLoading] = useState(false);
    const { dispatch } = store;
    const [imageType, setImageType] = useState(0);
    const cameraReady = useCameraReady({
        component: "ManualCapture",
        invitationId: responseScan?.invitation_id,
    });

    const capturePhoto = () => {
        window.AcuantCamera.startManualCapture(cameraCallback);
    };

    var cameraCallback = {
        onCaptured: (response) => {
            datadogLogs.logger.info(
                `Image captured (Manual Capture) with the invitationId: ${responseScan?.invitation_id}`,
                {
                    invitationId: responseScan?.invitation_id,
                    instance: instance,
                }
            );
            try {
                if (!response) {
                    setLoading(false);
                } else {
                    setLoading(true);
                }
            } catch (err) {
                datadogLogs.logger.error(
                    `Error on captured image response (Manual Capture) with the invitationId: ${responseScan?.invitation_id}`,
                    {
                        message: "Error on captured image response",
                        error: err.message,
                        invitationId: responseScan?.invitation_id,
                        instance: instance,
                    }
                );
            }
        },
        onCropped: (response) => {
            datadogLogs.logger.info(
                `Image cropped (Manual Capture) with the invitationId: ${responseScan?.invitation_id}`,
                {
                    invitationId: responseScan?.invitation_id,
                    instance: instance,
                    cardType: response?.cardType,
                    dpi: response?.dpi,
                    glare: response?.glare,
                    moire: response?.moire,
                    moireraw: response?.moireraw,
                    sharpness: response?.sharpness,
                }
            );
            try {
                if (response) {
                    //cropped response;
                    if (!imageToken) {
                        setLoading(false);
                        setImageType(response?.cardType);
                        setImageFront(response);
                    } else {
                        setLoading(false);
                        setImageBack(response);
                    }
                }
            } catch (err) {
                datadogLogs.logger.error(
                    `Error Image cropped (Manual Capture) with the invitationId: ${responseScan?.invitation_id}`,
                    {
                        invitationId: responseScan?.invitation_id,
                        instance: instance,
                        message: "Error cropping image",
                        error: err.message,
                    }
                );
            }
        },
        onFrameAvailable: (response) => { },
        onError: (error, code) => {
            datadogLogs.logger.error(
                `Error capturing image (Manual Capture) with the invitationId: ${responseScan?.invitation_id}`,
                {
                    invitationId: responseScan?.invitation_id,
                    instance: instance,
                    message: "Error capturing image",
                    error: error.message,
                    code: code,
                }
            );
        },
    };

    const dataURLToBlob = (image) => {
        let binary = atob(image);
        let array = [];
        for (let i = 0; i < binary.length; i++) {
            array.push(binary.charCodeAt(i));
        }
        return new Blob([new Uint8Array(array)], { type: "image/jpg" });
    };

    const queryData = async (params) => {
        setLoading(true);
        try {
            const base64Data = params?.image.split(",")[1];
            const data = {
                side: params?.side,
                instance: instance?.instance,
                image: dataURLToBlob(base64Data),
                AcuantApiSubscriptionId: responseScan?.acuant_api_subscription_id,
            };

            const postImg = await dispatch(postImage({ data }));

            if (postImg?.payload?.error) {

                datadogLogs.logger.error(
                    `Error posting image to ACUANT API (Manual Capture) with the invitationId: ${responseScan?.invitation_id}`,
                    {
                        invitationId: responseScan?.invitation_id,
                        message: "Error posting image to ACUANT API",
                        error: postImg?.payload?.error,
                        side: params?.side,
                        instance: instance,
                    }
                );
            } else {
                toast.error("Error posting image, please try again later.");
                datadogLogs.logger.error(
                    `Error posting image to ACUANT API (Manual Capture) with the invitationId: ${responseScan?.invitation_id}`,
                    {
                        message: "Error posting image to ACUANT API",
                        invitationId: responseScan?.invitation_id,
                        instance: instance,
                        side: params?.side,
                    }
                );
                if (params?.side === 0 && imageType === 1) {
                    setImageToken(true);
                } else {
                    setOpenSelfie(true);
                }
            }
        } catch (error) {
            toast.error("Error posting image, please try again later.");
            datadogLogs.logger.error(
                `Error posting image to ACUANT API (Manual Capture) with the invitationId: ${responseScan?.invitation_id}`,
                {
                    message: "Error posting image to ACUANT API",
                    invitationId: responseScan?.invitation_id,
                    instance: instance,
                    side: params?.side,
                    error: error,
                }
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {openSelfie ? (
                <>
                    <CaptureSelfie
                        instance={instance}
                        responseScan={responseScan}
                        tenantColor={tenantColor}
                        tenantImage={tenantImage}
                        tenantName={tenantName}
                    />
                </>
            ) : (
                <>
                    {!imageToken ? (
                        <section className="flex flex-col justify-center gap-6 items-center">
                            <Header
                                responseScan={responseScan}
                                tenantColor={tenantColor}
                                tenantImage={tenantImage}
                                tenantName={tenantName}
                            />
                            {imageFront?.image && <ImagePreview data={imageFront} />}
                            {loading && (
                                <section className="flex justify-center items-center absolute inset-0 flex-col gap-3 container px-3 py-5 bg-[#F8F7F6]">
                                    <AnalyzingLoader
                                        loading={loading}
                                        responseScan={responseScan}
                                        tenantColor={tenantColor}
                                        tenantImage={tenantImage}
                                        tenantName={tenantName}
                                    />
                                </section>
                            )}
                            <div>
                                {imageFront?.image ? (
                                    <>
                                        {!loading && (
                                            <div className="flex flex-col gap-4 w-full">
                                                {imageFront?.dpi < 430 ||
                                                    imageFront?.sharpness < 50 ||
                                                    imageFront?.glare < 50 ? (
                                                    <div className="absolute inset-x-0 bottom-6 flex justify-center items-center">
                                                        <button
                                                            className={`shadow-md py-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                                                            style={{
                                                                backgroundColor: tenantColor,
                                                            }}
                                                            onClick={() => capturePhoto()}
                                                            disabled={!cameraReady}
                                                        >
                                                            Retry <FiArrowRight size={20} />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="absolute inset-x-0 bottom-6 flex justify-center items-center">
                                                        <button
                                                            className={`shadow-md py-2`}
                                                            style={{
                                                                backgroundColor: tenantColor,
                                                            }}
                                                            onClick={() =>
                                                                queryData({
                                                                    side: 0,
                                                                    image: imageFront?.image?.data,
                                                                })
                                                            }
                                                        >
                                                            Continue <FiArrowRight size={20} />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {!loading && (
                                            <div className="flex flex-col w-full items-center justify-center">
                                                <div className=" flex flex-col justify-center items-center">
                                                    <h2 className="text-2xl text-center">
                                                        Take a picture of the front of your ID.
                                                    </h2>
                                                    {/* Icon Card Front */}
                                                    <div className="my-3">
                                                        <svg
                                                            width="343"
                                                            height="212"
                                                            viewBox="0 0 343 212"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                d="M4 12C4 7.58172 7.58172 4 12 4H331C335.418 4 339 7.58172 339 12V200C339 204.418 335.418 208 331 208H12C7.58172 208 4 204.418 4 200V12Z"
                                                                fill="#EBE4F7"
                                                                stroke={tenantColor}
                                                                strokeWidth="8"
                                                            />
                                                            <rect
                                                                x="44"
                                                                y="47"
                                                                width="100"
                                                                height="118"
                                                                rx="4"
                                                                fill="#EBE4F7"
                                                                stroke={tenantColor}
                                                                strokeWidth="8"
                                                            />
                                                            <circle
                                                                cx="94"
                                                                cy="90"
                                                                r="24"
                                                                fill="white"
                                                                stroke={tenantColor}
                                                                strokeWidth="8"
                                                            />
                                                            <path
                                                                d="M144 165H44C44.0126 137.936 65.9562 116 93.0233 116H94.9767C122.044 116 143.987 137.936 144 165Z"
                                                                fill="white"
                                                                stroke={tenantColor}
                                                                strokeWidth="8"
                                                            />
                                                            <path
                                                                fillRule="evenodd"
                                                                clipRule="evenodd"
                                                                d="M179 58C179 56.8954 179.895 56 181 56H253C254.105 56 255 56.8954 255 58V66C255 67.1046 254.105 68 253 68H181C179.895 68 179 67.1046 179 66V58ZM179 82C179 80.8954 179.895 80 181 80H301C302.105 80 303 80.8954 303 82V90C303 91.1046 302.105 92 301 92H181C179.895 92 179 91.1046 179 90V82ZM181 120C179.895 120 179 120.895 179 122V130C179 131.105 179.895 132 181 132H213C214.105 132 215 131.105 215 130V122C215 120.895 214.105 120 213 120H181ZM181 144C179.895 144 179 144.895 179 146V154C179 155.105 179.895 156 181 156H301C302.105 156 303 155.105 303 154V146C303 144.895 302.105 144 301 144H181ZM227 122C227 120.895 227.895 120 229 120H261C262.105 120 263 120.895 263 122V130C263 131.105 262.105 132 261 132H229C227.895 132 227 131.105 227 130V122Z"
                                                                fill={tenantColor}
                                                            />
                                                        </svg>
                                                    </div>
                                                    <div className="mb-3 mx-5">
                                                        <ul className="list-disc text-start text-sm font-default font-bold">
                                                            <li>
                                                                <p className="mb-1.5">
                                                                    Place ID on a flat surface with a solid
                                                                    background
                                                                </p>
                                                            </li>
                                                            <li>
                                                                <p className="mb-1.5">
                                                                    Avoid overhead LED and fluorescent lights
                                                                </p>
                                                            </li>
                                                            <li>
                                                                <p className="mb-1.5">
                                                                    Keep a steady hand. Image must be in focus
                                                                </p>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </div>

                                                <div className="absolute inset-x-0 bottom-6  flex justify-center items-center">
                                                    <button
                                                        className={`shadow-md py-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                                                        style={{
                                                            backgroundColor: tenantColor,
                                                        }}
                                                        onClick={() => capturePhoto()}
                                                        disabled={!cameraReady}
                                                    >
                                                        Continue <FiArrowRight size={20} />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </section>
                    ) : (
                        <section className="flex flex-col justify-center gap-6 items-center">
                            <Header
                                responseScan={responseScan}
                                tenantColor={tenantColor}
                                tenantImage={tenantImage}
                                tenantName={tenantName}
                            />
                            {imageBack?.image && <ImagePreview data={imageBack} />}
                            {loading && (
                                <section className="flex justify-center items-center absolute inset-0 flex-col gap-3 container px-3 py-5 bg-[#F8F7F6]">
                                    <AnalyzingLoader
                                        loading={true}
                                        responseScan={responseScan}
                                        tenantColor={tenantColor}
                                        tenantImage={tenantImage}
                                        tenantName={tenantName}
                                    />
                                </section>
                            )}
                            <div>
                                {imageBack?.image ? (
                                    <>
                                        {!loading && (
                                            <div className="flex flex-col gap-4 w-full">
                                                {imageBack?.dpi < 430 ||
                                                    imageBack?.sharpness < 50 ||
                                                    imageBack?.glare < 50 ? (
                                                    <div className="absolute inset-x-0 bottom-6 flex justify-center items-center">
                                                        <button
                                                            className={`shadow-md py-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                                                            style={{
                                                                backgroundColor: tenantColor,
                                                            }}
                                                            onClick={() => capturePhoto()}
                                                            disabled={!cameraReady}
                                                        >
                                                            Retry <FiArrowRight size={20} />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="absolute inset-x-0 bottom-6 flex justify-center items-center">
                                                        <button
                                                            className={`shadow-md py-2`}
                                                            style={{
                                                                backgroundColor: tenantColor,
                                                            }}
                                                            onClick={() =>
                                                                queryData({
                                                                    side: 1,
                                                                    image: imageBack?.image?.data,
                                                                })
                                                            }
                                                        >
                                                            Continue <FiArrowRight size={20} />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {!loading && (
                                            <div className="flex flex-col w-full items-center justify-center">
                                                <div className=" flex flex-col justify-center items-center">
                                                    <h2 className="text-2xl text-center">
                                                        Take a picture of the back of your ID.
                                                    </h2>
                                                    {/* Icon Card Back */}
                                                    <div className="my-3">
                                                        <svg
                                                            width="343"
                                                            height="212"
                                                            viewBox="0 0 343 212"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                d="M4 12C4 7.58172 7.58172 4 12 4H331C335.418 4 339 7.58172 339 12V200C339 204.418 335.418 208 331 208H12C7.58172 208 4 204.418 4 200V12Z"
                                                                fill="#EBE4F7"
                                                                stroke={tenantColor}
                                                                strokeWidth="8"
                                                            />
                                                            <rect
                                                                x="36"
                                                                y="36"
                                                                width="271"
                                                                height="62"
                                                                rx="4"
                                                                fill="white"
                                                                stroke={tenantColor}
                                                                strokeWidth="8"
                                                            />
                                                            <path
                                                                fillRule="evenodd"
                                                                clipRule="evenodd"
                                                                d="M46 44C44.8954 44 44 44.8954 44 46V88C44 89.1046 44.8954 90 46 90H48C49.1046 90 50 89.1046 50 88V46C50 44.8954 49.1046 44 48 44H46ZM60 44C58.8954 44 58 44.8954 58 46V88C58 89.1046 58.8954 90 60 90H62C63.1046 90 64 89.1046 64 88V46C64 44.8954 63.1046 44 62 44H60ZM68 46C68 44.8954 68.8954 44 70 44H72C73.1046 44 74 44.8954 74 46V88C74 89.1046 73.1046 90 72 90H70C68.8954 90 68 89.1046 68 88V46ZM82 44C80.8954 44 80 44.8954 80 46V88C80 89.1046 80.8954 90 82 90H84C85.1046 90 86 89.1046 86 88V46C86 44.8954 85.1046 44 84 44H82ZM94 46C94 44.8954 94.8954 44 96 44H98C99.1046 44 100 44.8954 100 46V88C100 89.1046 99.1046 90 98 90H96C94.8954 90 94 89.1046 94 88V46ZM106 44C104.895 44 104 44.8954 104 46V88C104 89.1046 104.895 90 106 90H108C109.105 90 110 89.1046 110 88V46C110 44.8954 109.105 44 108 44H106ZM114 46C114 44.8954 114.895 44 116 44H118C119.105 44 120 44.8954 120 46V88C120 89.1046 119.105 90 118 90H116C114.895 90 114 89.1046 114 88V46ZM128 44C126.895 44 126 44.8954 126 46V88C126 89.1046 126.895 90 128 90H130C131.105 90 132 89.1046 132 88V46C132 44.8954 131.105 44 130 44H128ZM140 46C140 44.8954 140.895 44 142 44H144C145.105 44 146 44.8954 146 46V88C146 89.1046 145.105 90 144 90H142C140.895 90 140 89.1046 140 88V46ZM152 44C150.895 44 150 44.8954 150 46V88C150 89.1046 150.895 90 152 90H154C155.105 90 156 89.1046 156 88V46C156 44.8954 155.105 44 154 44H152ZM162 46C162 44.8954 162.895 44 164 44H166C167.105 44 168 44.8954 168 46V88C168 89.1046 167.105 90 166 90H164C162.895 90 162 89.1046 162 88V46ZM174 44C172.895 44 172 44.8954 172 46V88C172 89.1046 172.895 90 174 90H176C177.105 90 178 89.1046 178 88V46C178 44.8954 177.105 44 176 44H174ZM186 46C186 44.8954 186.895 44 188 44H190C191.105 44 192 44.8954 192 46V88C192 89.1046 191.105 90 190 90H188C186.895 90 186 89.1046 186 88V46ZM202 44C200.895 44 200 44.8954 200 46V88C200 89.1046 200.895 90 202 90H204C205.105 90 206 89.1046 206 88V46C206 44.8954 205.105 44 204 44H202ZM210 46C210 44.8954 210.895 44 212 44H214C215.105 44 216 44.8954 216 46V88C216 89.1046 215.105 90 214 90H212C210.895 90 210 89.1046 210 88V46ZM224 44C222.895 44 222 44.8954 222 46V88C222 89.1046 222.895 90 224 90H226C227.105 90 228 89.1046 228 88V46C228 44.8954 227.105 44 226 44H224ZM234 46C234 44.8954 234.895 44 236 44H238C239.105 44 240 44.8954 240 46V88C240 89.1046 239.105 90 238 90H236C234.895 90 234 89.1046 234 88V46ZM246 44C244.895 44 244 44.8954 244 46V88C244 89.1046 244.895 90 246 90H248C249.105 90 250 89.1046 250 88V46C250 44.8954 249.105 44 248 44H246ZM258 46C258 44.8954 258.895 44 260 44H262C263.105 44 264 44.8954 264 46V88C264 89.1046 263.105 90 262 90H260C258.895 90 258 89.1046 258 88V46ZM270 44C268.895 44 268 44.8954 268 46V88C268 89.1046 268.895 90 270 90H272C273.105 90 274 89.1046 274 88V46C274 44.8954 273.105 44 272 44H270ZM280 46C280 44.8954 280.895 44 282 44H284C285.105 44 286 44.8954 286 46V88C286 89.1046 285.105 90 284 90H282C280.895 90 280 89.1046 280 88V46ZM294 44C292.895 44 292 44.8954 292 46V88C292 89.1046 292.895 90 294 90H296C297.105 90 298 89.1046 298 88V46C298 44.8954 297.105 44 296 44H294Z"
                                                                fill={tenantColor}
                                                            />
                                                            <path
                                                                fillRule="evenodd"
                                                                clipRule="evenodd"
                                                                d="M32 146C32 144.895 32.8954 144 34 144H66C67.1046 144 68 144.895 68 146V154C68 155.105 67.1046 156 66 156H34C32.8954 156 32 155.105 32 154V146ZM32 170C32 168.895 32.8954 168 34 168H154C155.105 168 156 168.895 156 170V178C156 179.105 155.105 180 154 180H34C32.8954 180 32 179.105 32 178V170ZM82 144C80.8954 144 80 144.895 80 146V154C80 155.105 80.8954 156 82 156H114C115.105 156 116 155.105 116 154V146C116 144.895 115.105 144 114 144H82Z"
                                                                fill={tenantColor}
                                                            />
                                                            <path
                                                                fillRule="evenodd"
                                                                clipRule="evenodd"
                                                                d="M203 170C203 168.895 203.895 168 205 168H207C208.105 168 209 168.895 209 170V178C209 179.105 208.105 180 207 180H205C203.895 180 203 179.105 203 178V170ZM215 170C215 168.895 215.895 168 217 168H219C220.105 168 221 168.895 221 170V178C221 179.105 220.105 180 219 180H217C215.895 180 215 179.105 215 178V170ZM227 168C225.895 168 225 168.895 225 170V178C225 179.105 225.895 180 227 180H229C230.105 180 231 179.105 231 178V170C231 168.895 230.105 168 229 168H227ZM243 170C243 168.895 243.895 168 245 168H247C248.105 168 249 168.895 249 170V178C249 179.105 248.105 180 247 180H245C243.895 180 243 179.105 243 178V170ZM255 168C253.895 168 253 168.895 253 170V178C253 179.105 253.895 180 255 180H257C258.105 180 259 179.105 259 178V170C259 168.895 258.105 168 257 168H255ZM267 170C267 168.895 267.895 168 269 168H271C272.105 168 273 168.895 273 170V178C273 179.105 272.105 180 271 180H269C267.895 180 267 179.105 267 178V170ZM285 168C283.895 168 283 168.895 283 170V178C283 179.105 283.895 180 285 180H287C288.105 180 289 179.105 289 178V170C289 168.895 288.105 168 287 168H285ZM295 170C295 168.895 295.895 168 297 168H299C300.105 168 301 168.895 301 170V178C301 179.105 300.105 180 299 180H297C295.895 180 295 179.105 295 178V170ZM307 168C305.895 168 305 168.895 305 170V178C305 179.105 305.895 180 307 180H309C310.105 180 311 179.105 311 178V170C311 168.895 310.105 168 309 168H307Z"
                                                                fill={tenantColor}
                                                            />
                                                        </svg>
                                                    </div>
                                                    <div className="mb-3 mx-5">
                                                        <ul className="list-disc text-start text-sm font-default font-bold">
                                                            <li>
                                                                <p className="mb-1.5">
                                                                    Place ID on a flat surface with a solid
                                                                    background
                                                                </p>
                                                            </li>
                                                            <li>
                                                                <p className="mb-1.5">
                                                                    Avoid overhead LED and fluorescent lights
                                                                </p>
                                                            </li>
                                                            <li>
                                                                <p className="mb-1.5">
                                                                    Keep a steady hand. Image must be in focus
                                                                </p>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </div>

                                                <div className="absolute inset-x-0 bottom-6 flex justify-center items-center">
                                                    <button
                                                        className={`shadow-md py-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                                                        style={{
                                                            backgroundColor: tenantColor,
                                                        }}
                                                        onClick={() => capturePhoto()}
                                                        disabled={
                                                            instance === null || instance === undefined || !cameraReady
                                                        }
                                                    >
                                                        Continue <FiArrowRight size={20} />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </section>
                    )}
                </>
            )}
        </>
    );
};

export default ManualCapture;
